/**
 * Top-bar Download menu (design §9). Items render STRICTLY from
 * `DownloadAvailability`: Download Report + Download All Files, plus
 * "Download ETL File" (client 2026-09-09, Figma) — the ETL sheet is ALSO
 * bundled into the All Files ZIP and is no longer staging-only. The whole
 * menu is mounted only when `can.download` allows it (gated by the header).
 */
import { ChevronDown, Download, FileDown, Loader2 } from 'lucide-react'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import type { DownloadAvailability } from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { CloseItem, MENU_PANEL_CLASS, MenuRow } from '../HeaderMenus'
import type { DownloadsApi } from './useDownloads'

export function DownloadMenu({
	downloads,
	api,
	disabled = false,
}: {
	downloads: DownloadAvailability
	api: DownloadsApi
	/** No report yet (not submitted / still processing / failed): there is
	 * nothing meaningful to download, so the button renders inert + greyed. */
	disabled?: boolean
}) {
	const anyBusy = Boolean(api.busy.report || api.busy.zip || api.busy.etl)
	if (disabled) {
		return (
			<button
				type="button"
				disabled
				title="Available once the screening report is generated"
				className="flex h-11 cursor-not-allowed items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-5 text-sm font-medium text-[#9CA3AF] opacity-60"
			>
				<Download className="size-4 text-[#9CA3AF]" />
				Download
				<ChevronDown className="size-4 text-[#9CA3AF]" />
			</button>
		)
	}
	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="group flex h-11 cursor-pointer items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-4 text-sm font-medium text-[#32302F] transition-colors hover:bg-[#F6F7F5]">
				{anyBusy ? (
					<Loader2 className="size-4 animate-spin text-[#747474]" />
				) : (
					<Download className="size-4 text-[#747474]" />
				)}
				Download
				<ChevronDown className="size-4 text-[#747474] transition-transform duration-200 group-data-[state=open]:rotate-180" />
			</DropdownMenuTrigger>
			<DropdownMenuContent
				// Down-LEFT anchoring (design §9 / 'Pre-screen (3).png': the panel's
				// right edge aligns with the pill), matching the sibling menus.
				// Compact sizing (user feedback 2026-06-13): standard rows, tighter
				// panel — the large variant overwhelmed the top bar.
				align="end"
				sideOffset={10}
				className={cn(MENU_PANEL_CLASS, 'w-[260px]')}
			>
				<div className="mb-[17px]">
					<CloseItem />
				</div>
				{downloads.individualReport ? (
					<MenuRow
						tone="green"
						icon={<Download />}
						label={api.busy.report ? 'Preparing Report...' : 'Download Report'}
						onSelect={api.downloadReport}
					/>
				) : null}
				{downloads.allFilesZip ? (
					<MenuRow
						tone="rose"
						icon={<FileDown />}
						label={api.busy.zip ? 'Preparing Files...' : 'Download All Files'}
						onSelect={api.downloadAllFiles}
					/>
				) : null}
				<MenuRow
					iconSrc="/img/etl-file-download-icon.svg"
					label={api.busy.etl ? 'Preparing Sheet...' : 'Download ETL File'}
					onSelect={api.downloadEtl}
				/>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
